import { SearchApi } from '@backstage/plugin-search-react';
import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

/**
 * Deduplicates TechDocs results by preferring anchor URLs over base URLs.
 * Example: ['/docs/page/', '/docs/page/#section'] → ['/docs/page/#section']
 */
function deduplicateTechDocsResults(results: any[]): any[] {
  const techDocs = results.filter(r => r.type === 'techdocs');
  const others = results.filter(r => r.type !== 'techdocs');

  if (techDocs.length === 0) {
    return results;
  }

  // Group by base URL, keeping only the first anchor URL if it exists
  const baseUrlMap = new Map<string, any>();

  techDocs.forEach(result => {
    const location = result.document?.location || result.location;
    if (!location) {
      return;
    }

    const baseUrl = location.split('#')[0];
    const hasAnchor = location.includes('#');
    const existing = baseUrlMap.get(baseUrl);
    const existingLocation = existing?.document?.location || existing?.location;

    // Only add if: no existing entry, or we're an anchor replacing a non-anchor
    if (!existing || (hasAnchor && !existingLocation?.includes('#'))) {
      baseUrlMap.set(baseUrl, result);
    }
  });

  return [...baseUrlMap.values(), ...others];
}

export class DeduplicatingSearchApi implements SearchApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async query(query: {
    term: string;
    filters?: Record<string, string | string[]>;
    types?: string[];
    pageCursor?: string;
  }) {
    const baseUrl = await this.discoveryApi.getBaseUrl('search');
    const params = new URLSearchParams();

    if (query.term) {
      params.append('term', query.term);
    }
    if (query.pageCursor) {
      params.append('pageCursor', query.pageCursor);
    }

    query.types?.forEach(type => params.append('types', type));

    if (query.filters) {
      Object.entries(query.filters).forEach(([key, value]) => {
        const values = Array.isArray(value) ? value : [value];
        values.forEach(v => params.append(`filters[${key}]`, v));
      });
    }

    const response = await this.fetchApi.fetch(`${baseUrl}/query?${params}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Search API request failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const result = await response.json();
    const deduplicated = deduplicateTechDocsResults(result.results || []);

    return {
      ...result,
      results: deduplicated,
      numberOfResults: deduplicated.length,
    };
  }
}
